import React from 'react';
import { Link } from 'react-router-dom';

// Using React.FC to ensure proper component typing and children handling
interface FooterLinkProps {
  href: string;
  children: React.ReactNode;
}

const FooterLink: React.FC<FooterLinkProps> = ({ href, children }) => (
  <li>
    {href.startsWith('#') ? (
      <a href={href} className="text-gray-400 hover:text-white transition-colors duration-300 text-sm font-medium">
        {children}
      </a>
    ) : (
      <Link to={href} className="text-gray-400 hover:text-white transition-colors duration-300 text-sm font-medium">
        {children}
      </Link>
    )}
  </li>
);

interface SocialIconProps {
  href: string;
  children: React.ReactNode;
  'aria-label': string;
}

const SocialIcon: React.FC<SocialIconProps> = ({ href, children, 'aria-label': ariaLabel }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 text-gray-400 hover:bg-[#FF5C00] hover:text-white transition-all duration-300"
  >
    {children}
  </a>
);

const Footer: React.FC = () => {
  return (
    <footer id="contact" className="bg-[#021226] text-gray-300 border-t border-white/5">
      <div className="container mx-auto px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

          {/* Column 1: Brand Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img src="https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OVL+-+NewLogo.webp" alt="Omniviewlabs Logo" className="h-12 object-contain" />
              <div>
                <span className="text-2xl font-black tracking-tight text-white font-heading">Omniviewlabs</span>
                <p className="text-[10px] uppercase tracking-[0.2em] font-black -mt-1 text-[#FF5C00]">Leave nothing to imagination</p>
              </div>
            </div>
            <p className="text-gray-400 mt-4 text-sm leading-relaxed max-w-xs">
              We transform product catalogs into an interactive canvas powered by 3D and AI, bridging the gap between customer and product through photorealistic visualization.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div className="lg:pl-8">
            <h3 className="text-xs font-black text-white mb-6 uppercase tracking-[0.2em]">Quick Links</h3>
            <ul className="space-y-4">
              <FooterLink href="#about">About Us</FooterLink>
              <FooterLink href="#products">Product Suite</FooterLink>
              <FooterLink href="#blog">Latest Insights</FooterLink>
              <FooterLink href="#contact-us">Book a Demo</FooterLink>
            </ul>
          </div>

          {/* Column 3: Contact */}
          <div>
            <h3 className="text-xs font-black text-white mb-6 uppercase tracking-[0.2em]">Contact Hub</h3>
            <ul className="space-y-5 text-sm text-gray-400">
              <li className="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0 text-[#FF5C00]" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
                <span>OmniView Labs, <br />Ranka Colony Road, Bangalore, India</span>
              </li>
              <li className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 flex-shrink-0 text-[#FF5C00]" viewBox="0 0 20 20" fill="currentColor"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" /><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" /></svg>
                <a href="mailto:info@omniviewlabs.com" className="hover:text-white transition-colors font-medium">info@omniviewlabs.com</a>
              </li>
            </ul>
          </div>

          {/* Column 4: Follow Our Journey */}
          <div>
            <h3 className="text-xs font-black text-white mb-6 uppercase tracking-[0.2em]">Follow Our Journey</h3>
            <div className="flex space-x-3">
              <SocialIcon href="https://www.facebook.com/omniview.labs.official" aria-label="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" /></svg>
              </SocialIcon>
              <SocialIcon href="https://www.instagram.com/omniview_labs" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
              </SocialIcon>
              <SocialIcon href="https://www.linkedin.com/company/omniviewlabs" aria-label="LinkedIn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
              </SocialIcon>
            </div>
          </div>

        </div>

        <div className="mt-12 md:mt-16 border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-6 text-gray-500">
          <p className="text-[11px] font-bold uppercase tracking-widest">&copy; {new Date().getFullYear()} OmniView Labs. Designed for the Infinite Retail Space.</p>
          <div className="flex flex-wrap justify-center gap-6 text-[10px] font-bold uppercase tracking-widest">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/opus3d-privacy" className="hover:text-white transition-colors text-[#FF5C00]">Opus 3D Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
